import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/0a9a82b6-40c7-4e8a-bd5d-f994eb0e1bfe/images/6c55d830accb3537e1cdf9d1650095ef87a9952556b54d852e9624aa6c0bba2e.png";

const Section = ({ title, children, id }) => (
  <section className="mb-10" data-testid={`privacy-section-${id}`}>
    <h2 className="font-display text-2xl font-bold text-[#1B1B1B] mb-4">{title}</h2>
    <div className="space-y-3 text-[15px] leading-relaxed text-[#1B1B1B]/75">{children}</div>
  </section>
);

const Item = ({ label, children }) => (
  <p>
    <span className="font-semibold text-[#1B1B1B]">{label}:</span> {children}
  </p>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen iso-pattern" data-testid="privacy-page">
      {/* Nav */}
      <nav className="w-full px-6 md:px-12 py-6 flex items-center justify-between max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-3" data-testid="privacy-brand-home">
          <img src={LOGO} alt="ISO Market" className="h-10 w-10 object-contain rounded-xl" />
          <span className="font-display text-2xl font-bold tracking-tight text-[#1B1B1B]">
            ISO <span className="text-[#C65A3A]">Market</span>
          </span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#C65A3A] hover:text-[#a8482d] transition-colors"
          data-testid="privacy-back-link"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 pb-24">
        <div className="mt-6 mb-12">
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1B1B1B]" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-[#1B1B1B]/50">Last updated: July 2026</p>
          <p className="mt-6 text-lg text-[#1B1B1B]/75 leading-relaxed max-w-2xl">
            Your privacy matters. This page explains what we collect, why we collect it, and what you can do about it
            &mdash; in plain English, no legalese.
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-[#1B1B1B]/8 shadow-sm p-8 md:p-12">
          {/* Short version */}
          <div className="mb-10 rounded-2xl bg-[#5C6B4A]/10 border border-[#5C6B4A]/20 p-6" data-testid="privacy-short-version">
            <h2 className="font-display text-xl font-bold text-[#3f4a33] mb-3">The short version</h2>
            <div className="space-y-3 text-[15px] leading-relaxed text-[#1B1B1B]/75">
              <p>
                We only collect what we need to run ISO Market &mdash; the account you sign up with, the listings and
                messages you create, and the payments you make for our products (Boost placements and the Verified
                Collector badge).
              </p>
              <p>
                We do <span className="font-semibold">NOT</span> sell your data. We do{" "}
                <span className="font-semibold">NOT</span> use third-party advertising trackers. We do{" "}
                <span className="font-semibold">NOT</span> ship crash reporting or analytics SDKs.
              </p>
              <p>
                You can delete your account and all associated data at any time from{" "}
                <span className="font-semibold">Profile &rarr; Delete my account</span> inside the app.
              </p>
            </div>
          </div>

          <Section id="collect" title="Information we collect">
            <Item label="Account info">
              your email address, a hashed password (we never see or store the plaintext), and the display name you
              choose.
            </Item>
            <Item label="Profile info">
              optional city and state (2-letter US code) that you enter yourself for local pickup visibility.
            </Item>
            <Item label="Listings and content you post">
              ISO titles, descriptions, categories, and photos you attach.
            </Item>
            <Item label="Messages you send">
              chat text, photo attachments, and offers you exchange with other users. Messages between you and the other
              party are stored so both sides can revisit the conversation.
            </Item>
            <Item label="Purchase records">
              metadata about Boost purchases and the Verified Collector badge (product bought, price, timestamp, Stripe
              session ID). We never see or store your credit card number &mdash; Stripe handles that.
            </Item>
            <Item label="Trust & safety data">
              ratings you receive, reports you file or receive, and blocks you set. Kept so ratings persist on your
              profile and admins can review reports.
            </Item>
            <Item label="Push notification tokens">
              only if you allow push notifications. Used to send you offer alerts, deal updates, and rating reminders.
            </Item>
            <Item label="Technical basics">
              the IP address and user-agent string that any web/mobile app receives with every API request. Kept
              short-term for security and abuse prevention.
            </Item>
          </Section>

          <Section id="use" title="How we use it">
            <Item label="To operate the app">
              show your listings in the feed, deliver your chat messages, verify your login, and let you buy Boosts and
              the Verified badge.
            </Item>
            <Item label="To keep ISO Market safe">
              automatically screen listings and messages for profanity; let admins act on reports; enforce blocks
              between users.
            </Item>
            <Item label="To communicate with you">
              in-app notifications about offers, deals, ratings, and (if you opted in) matching push notifications on
              your device.
            </Item>
            <p>
              We do <span className="font-semibold">NOT</span> use your data to train AI models, sell to data brokers, or
              profile you for advertising.
            </p>
          </Section>

          <Section id="share" title="Who we share it with">
            <Item label="Stripe">
              processes all payments (Boost + Verified badge) and holds your card info directly &mdash; we never see it.
              Stripe's privacy notice:{" "}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#C65A3A] underline">
                https://stripe.com/privacy
              </a>
            </Item>
            <Item label="Other users">
              see the parts of your profile you make public: display name, verified badge, trust score, listings, and
              city/state if you set one. They also see messages, offers, and ratings you exchange with them.
            </Item>
            <Item label="Law enforcement">
              only if compelled by a valid legal request, or if we reasonably believe it's necessary to prevent serious
              harm.
            </Item>
            <p>
              We do <span className="font-semibold">NOT</span> sell your data to any third party for any purpose.
            </p>
          </Section>

          <Section id="where" title="Where your data lives">
            <p>
              Your data is stored in a MongoDB database in the United States, operated on our behalf by our cloud
              hosting provider.
            </p>
            <p>
              We use HTTPS/TLS for every request between your app and our servers. Passwords are hashed with bcrypt
              before storage.
            </p>
          </Section>

          <Section id="retention" title="How long we keep it">
            <Item label="Account data">
              for as long as your account is active. When you delete your account (Profile &rarr; Delete my account), we
              hard-delete your listings, saved searches, notifications, blocks, push tokens, conversations, and messages
              within minutes. Historical deal records are anonymized (your user ID replaced with "Deleted user") so the
              other party keeps their transaction record.
            </Item>
            <p>Backups may retain data for up to 30 days after deletion before being fully purged.</p>
            <p>
              Ratings you gave to others may remain visible on their profile after your account is deleted, but
              attributed anonymously.
            </p>
          </Section>

          <Section id="rights" title="Your rights">
            <p>You can view and edit your profile at any time from the Profile tab.</p>
            <p>
              You can delete your entire account and personal data from{" "}
              <span className="font-semibold">Profile &rarr; Delete my account</span>. This is permanent and takes
              effect immediately.
            </p>
            <p>
              You can block any other user from their profile screen; blocked users cannot message you or see your
              listings.
            </p>
            <p>You can revoke push-notification permission at any time from your device's system settings.</p>
            <p>
              For questions, corrections, or requests we can't handle in-app, email us at{" "}
              <a href="mailto:support@iso-market.com" className="text-[#C65A3A] underline">
                support@iso-market.com
              </a>{" "}
              and we'll respond within a reasonable time.
            </p>
          </Section>

          <Section id="children" title="Children">
            <p>
              ISO Market is not directed at children under 13, and we do not knowingly collect personal information from
              anyone under 13. If we learn we've collected information from a child under 13, we will delete it.
            </p>
          </Section>

          <Section id="changes" title="Changes to this policy">
            <p>
              We may update this policy as the app evolves. When we do, we'll update the "Last updated" date at the top
              of this page. If a change is material (e.g. we start collecting a new category of data), we'll notify you
              in-app before it takes effect.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Questions, complaints, or a request under a privacy law (GDPR, CCPA, etc.)? Email{" "}
              <a href="mailto:hello@iso-market.com" className="text-[#C65A3A] underline">
                hello@iso-market.com
              </a>{" "}
              and we'll get back to you.
            </p>
          </Section>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-[#C65A3A] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#a8482d] shadow-lg shadow-[#C65A3A]/20"
            data-testid="privacy-return-button"
          >
            <ArrowLeft size={16} /> Back to ISO Market
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
